import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeTable } from "./useRealtime";

// ── Types ──────────────────────────────────────────────────────────────────

export type CommitmentKind = "floor" | "needle" | "parked";
export type CommitmentState = "open" | "done" | "parked";
export type JobRisk = "high" | "med" | "low";

export interface CommandDay {
  id: string;
  user_id: string;
  day: string;
  cap_hours: number;
  green_at: string | null;
  proposed_at: string | null;
}

export interface CommandCommitment {
  id: string;
  user_id: string;
  day: string;
  title: string;
  kind: CommitmentKind;
  state: CommitmentState;
  waiting_on: string | null;
  source_channel: string | null;
  source_ref: string | null;
  item_ids: string[];
  job_risk: JobRisk | null;
  first_seen_at: string | null;
  effort_hours: number | null;
  rank: number;
  done_at: string | null;
}

export interface FloorCandidate {
  title: string;
  waiting_on: string | null;
  source_channel: string | null;
  source_ref: string | null;
  job_risk: JobRisk;
  effort_hours: number;
  item_ids: string[];
  reason: string;
}

export interface ParkedCandidate {
  title: string;
  item_ids: string[];
}

export interface Proposal {
  day: string;
  floor: FloorCandidate[];
  needle_candidates: FloorCandidate[];
  parked: ParkedCandidate[];
  cap_estimate_hours: number;
  message?: string;
}

export const todayStr = () => new Date().toISOString().split("T")[0];

// ── Read today's command day + commitments ──────────────────────────────────

export function useCommandToday() {
  useRealtimeTable("command_commitments", ["command-commitments"]);
  useRealtimeTable("command_days", ["command-day"]);
  const { user } = useAuth();
  const day = todayStr();

  const dayQuery = useQuery({
    queryKey: ["command-day", user?.id, day],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("command_days")
        .select("*")
        .eq("user_id", user.id)
        .eq("day", day)
        .maybeSingle();
      if (error) throw error;
      return data as CommandDay | null;
    },
    enabled: !!user,
  });

  const commitmentsQuery = useQuery({
    queryKey: ["command-commitments", user?.id, day],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("command_commitments")
        .select("*")
        .eq("user_id", user.id)
        .eq("day", day)
        .order("kind", { ascending: true })
        .order("rank", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CommandCommitment[];
    },
    enabled: !!user,
  });

  return { dayQuery, commitmentsQuery, day };
}

// ── Morning brain: ask propose-floor for candidates ─────────────────────────

export function useProposeFloor() {
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (): Promise<Proposal> => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("propose-floor", {
        body: {},
      });
      if (error) throw error;
      return data as Proposal;
    },
  });
}

// ── Accept a triaged set → persist commitments for today ────────────────────

export interface AcceptPayload {
  floor: FloorCandidate[];
  needle: FloorCandidate | null;
  parked: ParkedCandidate[];
}

export function useAcceptTriage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const day = todayStr();

  return useMutation({
    mutationFn: async (payload: AcceptPayload) => {
      if (!user) throw new Error("Not authenticated");

      // Replace today's not-yet-done commitments; keep anything already DONE so
      // re-triaging mid-day never erases completed work.
      const { error: delErr } = await supabase
        .from("command_commitments")
        .delete()
        .eq("user_id", user.id)
        .eq("day", day)
        .neq("state", "done");
      if (delErr) throw delErr;

      const rows: Array<Partial<CommandCommitment>> = [];
      payload.floor.forEach((f, i) =>
        rows.push({
          user_id: user.id,
          day,
          title: f.title,
          kind: "floor",
          state: "open",
          waiting_on: f.waiting_on,
          source_channel: f.source_channel,
          source_ref: f.source_ref,
          item_ids: f.item_ids ?? [],
          job_risk: f.job_risk,
          effort_hours: f.effort_hours,
          rank: i,
        })
      );
      if (payload.needle) {
        rows.push({
          user_id: user.id,
          day,
          title: payload.needle.title,
          kind: "needle",
          state: "open",
          waiting_on: payload.needle.waiting_on,
          source_channel: payload.needle.source_channel,
          source_ref: payload.needle.source_ref,
          item_ids: payload.needle.item_ids ?? [],
          job_risk: payload.needle.job_risk,
          effort_hours: payload.needle.effort_hours,
          rank: 0,
        });
      }
      payload.parked.forEach((p, i) =>
        rows.push({
          user_id: user.id,
          day,
          title: p.title,
          kind: "parked",
          state: "parked",
          item_ids: p.item_ids ?? [],
          rank: i,
        })
      );

      if (rows.length > 0) {
        const { error: insErr } = await supabase
          .from("command_commitments")
          .insert(rows);
        if (insErr) throw insErr;
      }

      // Reset the green stamp — a fresh triage is a fresh, un-won day.
      await supabase
        .from("command_days")
        .upsert(
          { user_id: user.id, day, green_at: null },
          { onConflict: "user_id,day" }
        );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["command-commitments"] });
      queryClient.invalidateQueries({ queryKey: ["command-day"] });
    },
  });
}

// ── Toggle a commitment done/open ───────────────────────────────────────────

export function useToggleCommitment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("command_commitments")
        .update({
          state: done ? "done" : "open",
          done_at: done ? new Date().toISOString() : null,
        })
        .eq("id", id)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["command-commitments"] });
    },
  });
}

// ── Set / clear the green ("you're done") stamp ─────────────────────────────

export function useSetGreen() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const day = todayStr();

  return useMutation({
    mutationFn: async (green: boolean) => {
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("command_days")
        .upsert(
          { user_id: user.id, day, green_at: green ? new Date().toISOString() : null },
          { onConflict: "user_id,day" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["command-day"] });
    },
  });
}

// ── Dalin summary ───────────────────────────────────────────────────────────

export interface DaySummary {
  success: boolean;
  day: string;
  body: string;
  message?: string;
}

export function useSummarizeDay() {
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (): Promise<DaySummary> => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("summarize-day", {
        body: {},
      });
      if (error) throw error;
      return data as DaySummary;
    },
  });
}

// ── Derived: is the day won? (Floor cleared + needle done) ──────────────────

export function computeWon(commitments: CommandCommitment[]): boolean {
  const floor = commitments.filter((c) => c.kind === "floor");
  const needle = commitments.filter((c) => c.kind === "needle");
  if (floor.length === 0) return false; // no floor set = no win yet
  const floorDone = floor.every((c) => c.state === "done");
  const needleDone = needle.length === 0 || needle.every((c) => c.state === "done");
  return floorDone && needleDone;
}
